import z from "zod";
import { COST_CALCULATION_TYPE } from "@miplatform/constants";
import { coerceToNumber } from "@miplatform/utils";

export const customerPriceLevelTabSchema = z.object({
  priceLevelId: z.union([z.string().uuid(), z.literal("None"), z.literal("")]),
  accountId: z.string().uuid(),
});

export const customerFloorsDropsTabSchema = z.object({
  accountId: z.string().uuid(),
  maxFloorId: z.string().uuid(),
  modifiedOn: z.string().optional(),
});

export const customerDrawSetupTabSchema = z.object({
  draws: z
    .array(
      z.object({
        name: z.string().min(1, "Name is required"),
        percentage: z.preprocess(
          (value) =>
            typeof value === "number" && Number.isNaN(value)
              ? undefined
              : value,
          z
            .number({
              required_error: "Percentage is required",
              invalid_type_error: "Percentage is required",
            })
            .max(100, "Percentage must be ≤ 100%")
        ),
        orderTemplateId: z.string().uuid().nullable().or(z.literal("")),
      })
    )
    .superRefine((items, ctx) => {
      const uniqueValues = new Map();
      items.forEach((item, index) => {
        const firstIndex = uniqueValues.get(item.name);
        if (firstIndex !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Draw name must be unique`,
            path: [index, "name"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Draw name must be unique`,
            path: [firstIndex, "name"],
          });
          return;
        }
        uniqueValues.set(item.name, index);
      });
    }),
  drawTotal: z.number().refine((val) => val === 100, {
    message: "Total must equal 100%",
  }),
});

const dropDrawSchema = z.object({
  id: z.string().optional(),
  createdOn: z.string().optional(),
  modifiedOn: z.string().optional(),
  accountId: z.string().optional(),
  accountDrawId: z.string(),
  percentage: z.preprocess(
    (value) =>
      typeof value === "number" && Number.isNaN(value) ? undefined : value,
    z
      .number({
        required_error: "Percentage is required",
        invalid_type_error: "Percentage is required",
      })
      .max(100, "Percentage must be ≤ 100%")
  ),
});

const categoryDrawSchema = z.object({
  id: z.string().optional(),
  createdOn: z.string().optional(),
  modifiedOn: z.string().optional(),
  name: z.string().optional(),
  displayOrder: z.number().optional(),
  categoryId: z.string(),
  accountDrawId: z.string(),
  percentage: z.preprocess(
    (value) =>
      typeof value === "number" && Number.isNaN(value) ? undefined : value,
    z
      .number({
        required_error: "Percentage is required",
        invalid_type_error: "Percentage is required",
      })
      .max(100, "Percentage must be ≤ 100%")
  ),
});

const categoryPricingDetailsBaseSchema = z.object({
  id: z.string().optional(),
  createdOn: z.string().optional(),
  modifiedOn: z.string().optional(),
  categoryId: z.string(),
  categoryName: z.string(),
  accountId: z.string(),
  costCalculation: z.enum([
    COST_CALCULATION_TYPE.LIST,
    COST_CALCULATION_TYPE.NET_COST,
  ]),
});

const categoryPricingDrawsSchema = categoryPricingDetailsBaseSchema.extend({
  drawTotal: z.number().refine((val) => val === 100, {
    message: "Total must equal 100%",
  }),
  draws: z.array(categoryDrawSchema),
});

const categoryPricingModifierSchema = z.discriminatedUnion("costCalculation", [
  categoryPricingDetailsBaseSchema.extend({
    costCalculation: z.literal(COST_CALCULATION_TYPE.LIST),
    multiplier: z
      .preprocess(
        coerceToNumber,
        z.number({
          required_error: "Multiplier is required",
          invalid_type_error: "Multiplier is required",
        })
      )
      .pipe(
        z
          .number()
          .min(0.01, "Multiplier must be > 0.00")
          .max(10.0, "Multiplier must be ≤ 10.00")
      ),
    markup: z.number().nullable().optional(),
  }),
  categoryPricingDetailsBaseSchema.extend({
    costCalculation: z.literal(COST_CALCULATION_TYPE.NET_COST),
    markup: z
      .preprocess(
        coerceToNumber,
        z.number({
          required_error: "Markup is required",
          invalid_type_error: "Markup is required",
        })
      )
      .pipe(
        z
          .number()
          .min(-100, "Markup must be ≥ -100%")
          .max(100, "Markup must be ≤ 100%")
      ),
    multiplier: z.number().nullable().optional(),
  }),
]);

export const customerDrawsDistributionTabSchema = z.object({
  dropDistribution: z
    .object({
      draws: z.array(dropDrawSchema),
      dropTotal: z.number().refine((val) => val === 100, {
        message: "Total must equal 100%",
      }),
    })
    .optional(),
  categoryDistributions: z.array(categoryPricingDrawsSchema),
});

export const customerLineItemsTabsSchema = z.object({
  accountId: z.string().uuid(),
  name: z.string().min(1, "Name is required"),
  footnote: z.string().optional(),
  categoryIds: z
    .array(z.string().uuid())
    .min(1, "At least one category must be selected"),
  bidLineItemType: z.enum(["Bid", "Option"]),
});

const categoryFloorDropTypesSchema = z.object({
  categoryId: z.string().uuid(),
  dropTypeId: z.string().uuid(),
  dropTypeName: z.string(),
  floorId: z.string().uuid(),
});

export const customerCategoryFloorDropTypesSchema = z.array(
  categoryFloorDropTypesSchema
);

export const customerFloorsDropsFormSchema = z.object({
  maxFloor: customerFloorsDropsTabSchema,
  categoryFloorDropTypes: customerCategoryFloorDropTypesSchema,
});

export const customerItemsAssignTabSchema = z.record(
  z.string().uuid(),
  z.array(z.string().uuid())
);

export const customerItemsPricingTabSchema = z.object({
  categoryPricingModifiers: z.array(categoryPricingModifierSchema),
  itemPricingByCategory: z.record(
    z.string().uuid(),
    z.array(
      z.object({
        id: z.string().uuid(),
        manufacturerListPrice: z.number().nullish(),
        cost: z.number().nullish(),
        netSalesPrice: z.number().nullish(),
        marginPercent: z.number().nullish(),
        marginDollar: z.number().nullish(),
      })
    )
  ),
});
